import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../i18n";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { getApiError } from "../api/errors";
import { createInventory, getInventory, updateInventory } from "../api/inventory";
import {
  createProductOption,
  createProductOptionValue,
  createProductVariant,
  deleteProductOption,
  deleteProductOptionValue,
  getProductVariantConfiguration,
  updateProductOption,
  updateProductOptionValue,
  updateProductVariant,
} from "../api/products";
import { useAuth } from "../context/AuthContext";
import { formatMoney } from "../utils/format";
import {
  cartesianVariantCombinations,
  variantSignature,
} from "../utils/productVariants";
import ProductBarcodeDialog from "./ProductBarcodeDialog";
import VariantImageDialog from "./VariantImageDialog";

function optionalMoney(value) {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : NaN;
}

function OptionEditor({ productId, option, mutationsLocked, onChanged, onError }) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const addValue = async () => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      await createProductOptionValue(productId, option.public_id, { value: value.trim() });
      setValue("");
      await onChanged();
    } catch (error) {
      onError(getApiError(error, t("variants.addValueError")));
    } finally {
      setBusy(false);
    }
  };

  const renameOption = async () => {
    const name = window.prompt(t("variants.optionName"), option.name);
    if (!name?.trim() || name.trim() === option.name) return;
    setBusy(true);
    try {
      await updateProductOption(productId, option.public_id, { name: name.trim() });
      await onChanged();
    } catch (error) {
      onError(getApiError(error, t("variants.renameOptionError")));
    } finally {
      setBusy(false);
    }
  };

  const renameValue = async (item) => {
    const next = window.prompt(t("variants.renameValue", { name: option.name }), item.value);
    if (!next?.trim() || next.trim() === item.value) return;
    setBusy(true);
    try {
      await updateProductOptionValue(productId, option.public_id, item.public_id, {
        value: next.trim(),
      });
      await onChanged();
    } catch (error) {
      onError(getApiError(error, t("variants.renameValueError")));
    } finally {
      setBusy(false);
    }
  };

  const removeOption = async () => {
    if (!window.confirm(t("variants.deleteOption", { name: option.name }))) return;
    setBusy(true);
    try {
      await deleteProductOption(productId, option.public_id);
      await onChanged();
    } catch (error) {
      onError(getApiError(error, t("variants.deleteOptionError")));
    } finally {
      setBusy(false);
    }
  };

  const removeValue = async (item) => {
    if (!window.confirm(t("variants.deleteValue", { value: item.value, name: option.name }))) return;
    setBusy(true);
    try {
      await deleteProductOptionValue(productId, option.public_id, item.public_id);
      await onChanged();
    } catch (error) {
      onError(getApiError(error, t("variants.deleteValueError")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box>
            <Typography fontWeight={900}>{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("variants.values", { count: option.values.length })}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.25}>
            <Tooltip title={t("variants.renameOption")}><span><IconButton aria-label={t("variants.renameNamed", { name: option.name })} disabled={busy} onClick={renameOption}><EditRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title={t(mutationsLocked ? "variants.usedByVariants" : "variants.deleteOptionLabel")}><span><IconButton color="error" aria-label={t("variants.deleteNamed", { name: option.name })} disabled={busy || mutationsLocked} onClick={removeOption}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          </Stack>
        </Stack>
        <Stack direction="row" useFlexGap sx={{ my: 1.75, gap: 0.75, flexWrap: "wrap" }}>
          {option.values.map((item) => (
            <Chip
              key={item.public_id}
              label={item.value}
              onClick={() => renameValue(item)}
              onDelete={mutationsLocked ? undefined : () => removeValue(item)}
              deleteIcon={<DeleteOutlineRoundedIcon />}
              variant="outlined"
            />
          ))}
          {!option.values.length && <Typography variant="body2" color="text.secondary">{t("variants.firstValue")}</Typography>}
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            size="small"
            fullWidth
            label={t("variants.newValue", { name: option.name })}
            value={value}
            disabled={busy}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addValue();
              }
            }}
          />
          <Button variant="outlined" startIcon={<AddRoundedIcon />} disabled={busy || !value.trim()} onClick={addValue}>{t("variants.addValue")}</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function OverrideField({ label, enabled, value, inherited, onEnabled, onValue }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ minWidth: 0 }}>
      <FormControlLabel
        control={<Checkbox size="small" checked={enabled} onChange={(event) => onEnabled(event.target.checked)} />}
        label={<Typography variant="body2" fontWeight={800}>{t("variants.override", { label })}</Typography>}
      />
      <TextField
        size="small"
        fullWidth
        type="number"
        label={enabled ? label : t("variants.inherited", { value: inherited ?? "—" })}
        value={enabled ? value : ""}
        disabled={!enabled}
        slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
        onChange={(event) => onValue(event.target.value)}
      />
    </Box>
  );
}

function VariantEditor({ product, variant, inventory, store, onChanged, onError, onBarcode, onImage }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => ({
    sku: variant.sku || "",
    is_active: variant.is_active,
    priceEnabled: variant.price_override !== null,
    price: variant.price_override ?? "",
    wholesaleEnabled: variant.wholesale_price !== null,
    wholesale: variant.wholesale_price ?? "",
    costEnabled: variant.cost_price_override !== null,
    cost: variant.cost_price_override ?? "",
    minimumEnabled: variant.minimum_price_override !== null,
    minimum: variant.minimum_price_override ?? "",
  }));
  const [stock, setStock] = useState(() => ({
    quantity: String(inventory?.quantity ?? 0),
    minimum_stock: String(inventory?.minimum_stock ?? 5),
    location: inventory?.location || store?.name || "Main Store",
  }));
  const [saving, setSaving] = useState(false);
  const [stockSaving, setStockSaving] = useState(false);
  const [saved, setSaved] = useState("");

  const saveVariant = async () => {
    const price = draft.priceEnabled ? optionalMoney(draft.price) : null;
    const wholesale = draft.wholesaleEnabled ? optionalMoney(draft.wholesale) : null;
    const cost = draft.costEnabled ? optionalMoney(draft.cost) : null;
    const minimum = draft.minimumEnabled ? optionalMoney(draft.minimum) : null;
    if ([price, wholesale, cost, minimum].some(Number.isNaN)) {
      onError(t("variants.priceError"));
      return;
    }
    setSaving(true);
    setSaved("");
    try {
      const response = await updateProductVariant(product.public_id, variant.public_id, {
        sku: draft.sku.trim() || null,
        is_active: draft.is_active,
        price_override: price,
        wholesale_price: wholesale,
        cost_price_override: cost,
        minimum_price_override: minimum,
      });
      const next = response.data;
      setDraft({
        sku: next.sku || "",
        is_active: next.is_active,
        priceEnabled: next.price_override !== null,
        price: next.price_override ?? "",
        wholesaleEnabled: next.wholesale_price !== null,
        wholesale: next.wholesale_price ?? "",
        costEnabled: next.cost_price_override !== null,
        cost: next.cost_price_override ?? "",
        minimumEnabled: next.minimum_price_override !== null,
        minimum: next.minimum_price_override ?? "",
      });
      setSaved(t("variants.variantSaved"));
      await onChanged({ preserveDrafts: true });
    } catch (error) {
      onError(getApiError(error, t("variants.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const saveStock = async () => {
    const quantity = Number(stock.quantity);
    const minimumStock = Number(stock.minimum_stock);
    if (!Number.isInteger(quantity) || quantity < 0 || !Number.isInteger(minimumStock) || minimumStock < 0) {
      onError(t("variants.stockError"));
      return;
    }
    setStockSaving(true);
    setSaved("");
    try {
      if (inventory) {
        await updateInventory(inventory.public_id, {
          quantity,
          minimum_stock: minimumStock,
          location: stock.location.trim() || store?.name || "Main Store",
          source: "manual",
        });
      } else {
        await createInventory({
          product_public_id: product.public_id,
          product_variant_public_id: variant.public_id,
          store_public_id: store.public_id,
          quantity,
          minimum_stock: minimumStock,
          location: stock.location.trim() || store.name,
          source: "manual",
        });
      }
      setSaved(t("variants.inventorySaved"));
      await onChanged({ preserveDrafts: true });
    } catch (error) {
      onError(getApiError(error, t("variants.inventoryError")));
    } finally {
      setStockSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, opacity: draft.is_active ? 1 : 0.72 }}>
      <CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "space-between", gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" useFlexGap sx={{ gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
              <Typography fontWeight={950}>{variant.label}</Typography>
              <Chip size="small" label={t(draft.is_active ? "common.active" : "common.inactive")} color={draft.is_active ? "success" : "default"} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t("variants.resolvedPriceCost", { price: formatMoney(variant.resolved_price), cost: variant.resolved_cost_price == null ? "—" : formatMoney(variant.resolved_cost_price) })}
            </Typography>
          </Box>
          <FormControlLabel control={<Switch checked={draft.is_active} onChange={(event) => setDraft((current) => ({ ...current, is_active: event.target.checked }))} />} label={t("variants.sellable")} />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={1.5}>
          <TextField size="small" label={t("variants.skuOptional")} value={draft.sku} onChange={(event) => setDraft((current) => ({ ...current, sku: event.target.value }))} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
            <OverrideField label={t("catalog.sellingPrice")} enabled={draft.priceEnabled} value={draft.price} inherited={variant.inherited_price} onEnabled={(enabled) => setDraft((current) => ({ ...current, priceEnabled: enabled }))} onValue={(value) => setDraft((current) => ({ ...current, price: value }))} />
            <OverrideField label={t("products.wholesalePrice")} enabled={draft.wholesaleEnabled} value={draft.wholesale} inherited={variant.inherited_wholesale_price} onEnabled={(enabled) => setDraft((current) => ({ ...current, wholesaleEnabled: enabled }))} onValue={(value) => setDraft((current) => ({ ...current, wholesale: value }))} />
            <OverrideField label={t("catalog.catalogCost")} enabled={draft.costEnabled} value={draft.cost} inherited={variant.inherited_cost_price} onEnabled={(enabled) => setDraft((current) => ({ ...current, costEnabled: enabled }))} onValue={(value) => setDraft((current) => ({ ...current, cost: value }))} />
            <OverrideField label={t("variants.minimumPrice")} enabled={draft.minimumEnabled} value={draft.minimum} inherited={variant.inherited_minimum_price} onEnabled={(enabled) => setDraft((current) => ({ ...current, minimumEnabled: enabled }))} onValue={(value) => setDraft((current) => ({ ...current, minimum: value }))} />
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={17} color="inherit" /> : <SaveRoundedIcon />} disabled={saving} onClick={saveVariant}>{t("variants.saveVariant")}</Button>
            <Button variant="outlined" startIcon={<QrCodeScannerRoundedIcon />} onClick={() => onBarcode(variant)}>{t("catalog.barcodes")}</Button>
            <Button
              variant="outlined"
              startIcon={<PhotoCameraRoundedIcon />}
              onClick={() => onImage(variant)}
            >
              {t(variant.image ? "variants.variantImage" : "variants.imageOverride")}
            </Button>
          </Stack>
          {product.track_inventory !== false && <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1.25, alignItems: "center" }}>
              <Inventory2RoundedIcon fontSize="small" color="primary" />
              <Typography fontWeight={900}>{t("variants.inventoryFor", { type: t(inventory ? "nav.inventory" : "variants.initialInventory"), store: store?.name || t("variants.selectedStore") })}</Typography>
            </Stack>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1.5fr auto" }, gap: 1 }}>
              <TextField size="small" type="number" label={t(inventory ? "common.quantity" : "catalog.initialStock")} value={stock.quantity} slotProps={{ htmlInput: { min: 0, step: 1 } }} onChange={(event) => setStock((current) => ({ ...current, quantity: event.target.value }))} />
              <TextField size="small" type="number" label={t("catalog.minimumStock")} value={stock.minimum_stock} slotProps={{ htmlInput: { min: 0, step: 1 } }} onChange={(event) => setStock((current) => ({ ...current, minimum_stock: event.target.value }))} />
              <TextField size="small" label={t("catalog.location")} value={stock.location} onChange={(event) => setStock((current) => ({ ...current, location: event.target.value }))} />
              <Button variant="outlined" disabled={stockSaving || !store} onClick={saveStock}>{t(stockSaving ? "variants.saving" : inventory ? "variants.updateStock" : "variants.createInitialInventory")}</Button>
            </Box>
          </Box>}
          {saved && <Alert severity="success" onClose={() => setSaved("")}>{saved}</Alert>}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProductVariantManager({ open, product, onClose, onChanged }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { selectedStore } = useAuth();
  const [configuration, setConfiguration] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [optionName, setOptionName] = useState("");
  const [selectedCombinations, setSelectedCombinations] = useState([]);
  const [barcodeVariant, setBarcodeVariant] = useState(null);
  const [imageVariantId, setImageVariantId] = useState(null);

  const productId = product?.public_id || "";

  const load = useCallback(async () => {
    if (!productId) return;
    const [configurationResponse, inventoryResponse] = await Promise.all([
      getProductVariantConfiguration(productId),
      getInventory({ product_public_id: productId, page_size: 100 }),
    ]);
    setConfiguration(configurationResponse.data);
    setInventory(inventoryResponse.data?.items || []);
  }, [productId]);

  useEffect(() => {
    if (!open || !productId) return;
    let active = true;
    setLoading(true);
    setError("");
    setSuccess("");
    load()
      .catch((requestError) => {
        if (active) setError(getApiError(requestError, t("variants.loadError")));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [load, open, productId, t]);

  // The manager remains mounted while its parent clears the selected product.
  // Never render an editor from a previous product or during the close transition.
  const activeConfiguration = open && productId && configuration?.product_public_id === productId
    ? configuration
    : null;
  const allCombinations = useMemo(
    () => cartesianVariantCombinations(activeConfiguration?.options || []),
    [activeConfiguration?.options],
  );
  const existingSignatures = useMemo(
    () => new Set((activeConfiguration?.variants || []).map((variant) => variantSignature(variant.option_value_public_ids))),
    [activeConfiguration?.variants],
  );
  const missingCombinations = allCombinations.filter((combination) => !existingSignatures.has(combination.signature));

  useEffect(() => {
    setSelectedCombinations((current) => current.filter((signature) =>
      missingCombinations.some((combination) => combination.signature === signature)));
  }, [activeConfiguration]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = async () => {
    await load();
    onChanged?.();
  };

  const addOption = async () => {
    if (!optionName.trim()) return;
    setBusy(true);
    setError("");
    try {
      await createProductOption(productId, { name: optionName.trim() });
      setOptionName("");
      setSuccess(t("variants.optionAdded"));
      await reload();
    } catch (requestError) {
      setError(getApiError(requestError, t("variants.addOptionError")));
    } finally {
      setBusy(false);
    }
  };

  const createSelected = async () => {
    const selected = missingCombinations.filter((item) => selectedCombinations.includes(item.signature));
    if (!selected.length) return;
    setBusy(true);
    setError("");
    let created = 0;
    try {
      for (const combination of selected) {
        await createProductVariant(productId, {
          option_value_public_ids: combination.optionValuePublicIds,
        });
        created += 1;
      }
      setSelectedCombinations([]);
      setSuccess(t("variants.created", { count: created }));
      await reload();
    } catch (requestError) {
      setError(getApiError(requestError, t("variants.partialCreate", { count: created })));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const variants = activeConfiguration?.variants || [];
  const imageVariant = variants.find((variant) => variant.public_id === imageVariantId) || null;

  return (
    <>
      <Dialog open={Boolean(open && productId)} onClose={() => { if (!busy) onClose?.(); }} fullScreen={fullScreen} fullWidth maxWidth="xl">
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Typography component="div" variant="h5" fontWeight={950}>{t("variants.manager")}</Typography>
          <Typography variant="body2" color="text.secondary">{product?.brand} {product?.name}</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#f8fafc", p: { xs: 1.5, sm: 2.5 } }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}
          {loading ? (
            <Box sx={{ minHeight: 320, display: "grid", placeItems: "center" }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2.25}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={950}>{t("variants.optionsHeading")}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("variants.optionsHelp")}
                  </Typography>
                  {!variants.length && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
                      <TextField size="small" fullWidth label={t("variants.newOption")} value={optionName} onChange={(event) => setOptionName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addOption(); } }} />
                      <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={busy || !optionName.trim()} onClick={addOption}>{t("variants.addOption")}</Button>
                    </Stack>
                  )}
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                    {(activeConfiguration?.options || []).map((option) => (
                      <OptionEditor key={option.public_id} productId={productId} option={option} mutationsLocked={variants.length > 0} onChanged={reload} onError={setError} />
                    ))}
                  </Box>
                  {!activeConfiguration?.options?.length && <Alert severity="info">{t("variants.startOption")}</Alert>}
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "space-between", gap: 1 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={950}>{t("variants.combinationsHeading")}</Typography>
                      <Typography variant="body2" color="text.secondary">{t("variants.combinationsHelp")}</Typography>
                    </Box>
                    <Button variant="contained" disabled={busy || !selectedCombinations.length} onClick={createSelected}>{t("variants.createSelected", { count: selectedCombinations.length })}</Button>
                  </Stack>
                  <Stack spacing={0.5} sx={{ mt: 1.5, maxHeight: 260, overflowY: "auto" }}>
                    {missingCombinations.map((combination) => (
                      <FormControlLabel
                        key={combination.signature}
                        control={<Checkbox checked={selectedCombinations.includes(combination.signature)} onChange={(event) => setSelectedCombinations((current) => event.target.checked ? [...current, combination.signature] : current.filter((item) => item !== combination.signature))} />}
                        label={combination.label}
                      />
                    ))}
                    {activeConfiguration?.options?.length > 0 && !missingCombinations.length && <Alert severity="success">{t("variants.allCreated")}</Alert>}
                    {activeConfiguration?.options?.some((option) => !option.values.length) && <Alert severity="warning">{t("variants.needValues")}</Alert>}
                  </Stack>
                </CardContent>
              </Card>

              <Box>
                <Typography variant="h6" fontWeight={950}>{t("variants.operationsHeading")}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t(product?.track_inventory === false ? "variants.operationsUntracked" : "variants.operationsTracked")}
                </Typography>
                <Stack spacing={1.5}>
                  {variants.map((variant) => (
                    <VariantEditor
                      key={variant.public_id}
                      product={product}
                      variant={variant}
                      inventory={inventory.find((item) => item.variant?.public_id === variant.public_id)}
                      store={selectedStore}
                      onChanged={reload}
                      onError={setError}
                      onBarcode={setBarcodeVariant}
                      onImage={(variant) => setImageVariantId(variant.public_id)}
                    />
                  ))}
                  {!variants.length && <Alert severity="info">{t("variants.noVariants")}</Alert>}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={onClose} disabled={busy}>{t("common.close")}</Button></DialogActions>
      </Dialog>
      <ProductBarcodeDialog open={Boolean(barcodeVariant)} product={product} variant={barcodeVariant} onClose={() => setBarcodeVariant(null)} />
      <VariantImageDialog
        open={Boolean(imageVariant)}
        product={product}
        variant={imageVariant}
        onClose={() => setImageVariantId(null)}
        onChanged={reload}
      />
    </>
  );
}
